import { StickerTrayCanvasDrawer } from "./StickerTrayCanvasDrawer";
import { v4 as uuidv4 } from "uuid";
import localforage from 'localforage';
import { CanvasDrawer } from "./CanvasDrawer";

export interface NamedImage {
    img: ImageBitmap,
    imgID: string
}

export const ImageStore = {
    namedImages: [] as NamedImage[],
    cacheName: 'image-cache' as string,
    rawCacheName: 'raw-image-cache' as string,
    MAX_IMAGE_DIMENSION: 4096,

    downsampleImage: async function (file: File): Promise<{ bitmap: ImageBitmap, blob: Blob }> {
        const originalBitmap = await createImageBitmap(file);

        const width = originalBitmap.width;
        const height = originalBitmap.height;
        const maxDim = this.MAX_IMAGE_DIMENSION;

        if (width <= maxDim && height <= maxDim) {
            return { bitmap: originalBitmap, blob: file };
        }

        let newWidth = width;
        let newHeight = height;

        if (width > height) {
            newWidth = maxDim;
            newHeight = Math.round((height / width) * maxDim);
        } else {
            newHeight = maxDim;
            newWidth = Math.round((width / height) * maxDim);
        }

        console.log(`Downsampling image from ${width}x${height} to ${newWidth}x${newHeight}`);

        const canvas = document.createElement('canvas');
        canvas.width = newWidth;
        canvas.height = newHeight;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
            console.warn('Failed to get 2d context for downsampling');
            originalBitmap.close();
            return { bitmap: await createImageBitmap(file), blob: file };
        }

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        ctx.drawImage(originalBitmap, 0, 0, newWidth, newHeight);

        const downsampledBlob = await new Promise<Blob>((resolve, reject) => {
            canvas.toBlob((blob) => {
                if (blob) resolve(blob);
                else reject(new Error('Failed to create blob from canvas'));
            }, 'image/png');
        });

        const downsampledBitmap = await createImageBitmap(canvas);

        originalBitmap.close();

        return { bitmap: downsampledBitmap, blob: downsampledBlob };
    },

    // rawFile is the original pre-background-removal upload (full color,
    // real background intact). Kept under the same imgId as the cutout, in
    // a separate cache, so a higher-quality remover (e.g. Apple Vision) can
    // reprocess it later - a bg-removed cutout has already lost the pixel
    // data a remover needs to do better than the client-side model did.
    addImage: async function (file: File, rawFile?: File): Promise<string | undefined> {

        try {
            const { bitmap: imageBitmap, blob: imageBlob } = await this.downsampleImage(file);

            const imgId = uuidv4()
            this.namedImages.unshift({ img: imageBitmap, imgID: imgId });

            console.log(`Added image with ID ${imgId}. Total images: ${this.namedImages.length}`);

            if (StickerTrayCanvasDrawer.canvas) {
                StickerTrayCanvasDrawer.redrawStickerTrayCanvas();
            }

            const cache = await caches.open(this.cacheName)

            const response = new Response(imageBlob, {
                headers: { "Content-Type": "image/png" },
            });

            await cache.put(imgId, response)

            const imageIds = await localforage.getItem<string[]>('images') ?? [];

            imageIds.unshift(imgId)

            await localforage.setItem('images', imageIds)

            if (rawFile) {
                const rawCache = await caches.open(this.rawCacheName);
                await rawCache.put(imgId, new Response(rawFile, {
                    headers: { "Content-Type": rawFile.type || "image/png" },
                }));

                const rawImageIds = await localforage.getItem<string[]>('rawImages') ?? [];
                rawImageIds.unshift(imgId);
                await localforage.setItem('rawImages', rawImageIds);
            }

            return imgId;

        } catch (error) {
            console.error("Error processing file:", error);
            return undefined;
        }
    },

    // Looks up the raw pre-background-removal blob for a sticker's imgId,
    // if one was saved. Used at checkout to upload originals for later
    // server-side reconstruction.
    getRawImageBlob: async function (imgId: string): Promise<Blob | null> {
        try {
            const rawCache = await caches.open(this.rawCacheName);
            const cachedResponse = await rawCache.match(imgId);
            if (!cachedResponse) return null;
            return await cachedResponse.blob();
        } catch (error) {
            console.error("Error reading raw image:", error);
            return null;
        }
    },

    sleep(ms: number) {
        return new Promise(resolve => setTimeout(resolve, ms));
    },

    async addImages(files: FileList) {
        const fileArray = Array.from(files);
        for (const file of fileArray) {
            await this.addImage(file);
            await this.sleep(200);
        }
    },

    init: async function () {
        try {
            const imageIds = (await localforage.getItem<string[]>('images')) ?? [];

            if (imageIds.length === 0) {
                console.log('No cached images found.');
                return;
            }

            const cache = await caches.open(this.cacheName);

            for (const imgId of imageIds) {
                const cachedResponse = await cache.match(imgId);
                if (!cachedResponse) {
                    console.warn(`No cache entry found for image ID: ${imgId}`);
                    continue;
                }

                const blob = await cachedResponse.blob();
                const imageBitmap = await createImageBitmap(blob);
                this.namedImages.push({ img: imageBitmap, imgID: imgId });
            }

            CanvasDrawer.rerenderCanvas()

            console.log(`Loaded ${this.namedImages.length} images from cache.`);

        } catch (error) {
            console.error('Failed to initialize ImageStore:', error);
        }
    }

};